import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatNumber } from '../../i18n/formatters';
import { categoryLabel } from './categoryLabel';
import AlignmentChip from '../shared/AlignmentChip';

function gradeLabel(min, max) {
  if (min == null && max == null) return null;
  const fmt = g => g === 0 ? i18n.t('printables:grade.kindergarten') : String(g);
  if (min === max) return i18n.t('printables:grade.single', { grade: fmt(min) });
  return i18n.t('printables:grade.range', { min: fmt(min), max: fmt(max) });
}

export default function PrintableCard({ poster, onClick, onPrint, onBuy, purchaseCost, alignment, viewerStateCode }) {
  const { t } = useTranslation('printables');
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const grade = gradeLabel(poster.gradeMin, poster.gradeMax);

  return (
    <div
      onClick={() => onClick(poster.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`bv-pcard${hovered ? ' is-hover' : ''}`}
    >
      <div className="bv-pcard__pic" style={{ position: 'relative', aspectRatio: '2/3', overflow: 'hidden' }}>
        {poster.thumbnailUrl && !imgError ? (
          <img
            src={poster.thumbnailUrl}
            alt={poster.title}
            loading="lazy"
            draggable={false}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="rgba(27,37,80,0.142)" strokeWidth="1.5" />
              <path d="M3 16l5-5 4 4 3-3 6 6" stroke="rgba(27,37,80,0.142)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        )}

        {hovered && poster.thumbnailUrl && !imgError && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(27,37,80,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'opacity 0.2s ease',
          }}>
            <span style={{
              color: '#fff', fontSize: '0.8rem', fontWeight: 600,
              padding: '8px 14px', borderRadius: 8,
              background: 'rgba(26,176,242,0.9)',
            }}>
              {t('card.view')}
            </span>
            {/* Exactly two actions on every printable (owner 2026-10-26): Save to My library and Print — owned or not
                (a printable owned in StudioCore may not be in the Brightivity library yet; saving it again costs nothing). */}
            {onBuy ? (
              <span
                onClick={(e) => { e.stopPropagation(); onBuy(poster); }}
                data-testid="card-save"
                style={{
                  color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                  padding: '8px 14px', borderRadius: 8,
                  background: 'rgba(127,75,246, 0.85)',
                  cursor: 'pointer',
                }}
              >
                {purchaseCost === 0
                  ? t('card.getFree')
                  : purchaseCost != null
                    ? t('card.buyWithCost', { cost: formatNumber(purchaseCost) })
                    : t('card.buy')}
              </span>
            ) : null}
            {onPrint ? (
              <span
                onClick={(e) => { e.stopPropagation(); onPrint(poster); }}
                data-testid="card-print"
                style={{
                  color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                  padding: '8px 14px', borderRadius: 8,
                  background: 'rgba(38,199,116,0.9)',
                  cursor: 'pointer',
                }}
              >
                {t('card.print')}
              </span>
            ) : null}
          </div>
        )}


      </div>

      <div className="bv-pcard__body">
        <div className="bv-pcard__title">
          {poster.title}
        </div>

        <div className="bv-pcard__tags">
          {poster.categoryName && (
            <span className="bv-tag bv-tag--green bv-pcard__cat">
              {categoryLabel(t, poster.categoryName)}
            </span>
          )}
          {grade && (
            <span className="bv-tag bv-tag--blue">
              {grade}
            </span>
          )}
          {/* IIG Phase 4 — standard-alignment chip (hidden when alignment absent). */}
          {alignment && <AlignmentChip alignment={alignment} viewerStateCode={viewerStateCode} />}
        </div>
      </div>
    </div>
  );
}
